import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { Activity, AlertTriangle, Loader2 } from "lucide-react";
import { MOCKS_ENABLED } from "../shared/api/mock/registry";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface PingResponse {
  status: string;
  service: string;
  time: string;
}

async function fetchPing(): Promise<PingResponse> {
  // In mock mode (default dev) don't touch the backend — it has no tenant
  // header/JWT yet, so the call only spawns TenantResolutionFilter warnings.
  // The real ping still runs when VITE_USE_MOCKS=false.
  if (MOCKS_ENABLED) {
    return {
      status: "ok",
      service: "his-api (mock)",
      time: new Date().toISOString(),
    };
  }
  const { data } = await axios.get<PingResponse>("/api/v1/_meta/ping");
  return data;
}

export function Ping() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["ping"],
    queryFn: fetchPing,
  });

  return (
    <main className="mx-auto w-full max-w-2xl p-6 sm:p-8">
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Activity className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-foreground">
            Addy HIS — Web shell
          </h1>
          <p className="text-sm text-muted-foreground">
            Kiểm tra kết nối API
          </p>
        </div>
      </div>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">API ping</CardTitle>
          {data && (
            <Badge variant={data.status === "ok" ? "success" : "warning"}>
              {data.status}
            </Badge>
          )}
        </CardHeader>
        <CardContent>
          {isLoading && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Pinging API…
            </p>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-destructive">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <pre className="whitespace-pre-wrap break-all text-sm">
                {String(error)}
              </pre>
            </div>
          )}

          {data && (
            <pre className="overflow-x-auto rounded-md bg-foreground p-4 text-sm text-background">
              {JSON.stringify(data, null, 2)}
            </pre>
          )}
        </CardContent>
      </Card>

      <p className="mt-4 text-sm text-muted-foreground">
        Phase 1 placeholder. Real shell, auth, and tenant context lands in W5–W6.
      </p>
    </main>
  );
}
